import { useQueryClient } from "@tanstack/react-query";
import { Pencil } from "lucide-react";
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { RouteComponentProps, withRouter, NavLink } from "react-router-dom";

import { PageTitle } from "../../components/Common/PageTitle";
import { InfiniteScroller } from "../../components/InfiniteScroller";
import { MediaCategorySection } from "../../components/MediaComponents/MediaCategorySection";
import { MediaItem } from "../../components/MediaComponents/MediaItem";
import {
  IUploadMediaDefaults,
  ModalUploadMedia,
} from "../../components/MediaComponents/ModalUploadMedia";
import { useMe } from "../../hooks/queries";
import { AccountType } from "../../models/AccountData";
import { FeatureUseLimitCalculator } from "../../models/featureUseLimits/FeatureUseLimitsCalculator";
import {
  FeatureUseStatus,
  OrgFeatures,
} from "../../models/featureUseLimits/FeatureUseLimitsData.const";
import { MediaData } from "../../models/MediaData";
import { MediaItemCategory } from "../../models/MediaItemCategory";
import DataSvc from "../../services/dataSvc";
import useLocation from "../FacilityDetailsPage/hooks/useLocation";

import "./styles.scss";

interface MatchParams {
  locationId?: string;
}

type IMediaPageProps = RouteComponentProps<MatchParams>;

const MediaPage: React.FunctionComponent<IMediaPageProps> = (props) => {
  const { t: _t } = useTranslation();
  const t = (key: string) => _t(`mediaPage.${key}`);

  const queryClient = useQueryClient();

  const locationId = props.match.params.locationId ?? "";

  const { data: locationData } = useLocation(locationId);
  const { data: me } = useMe();

  const [uploadModal, setUploadModal] = useState<IUploadMediaDefaults | null>(
    null
  );

  const canUploadMedia =
    me?.type === AccountType.ADMIN ||
    me?.type === AccountType.LOC_ADMIN ||
    (me?.type === AccountType.ORG_ADMIN &&
      FeatureUseLimitCalculator.getFeatureUseStatus(
        OrgFeatures.USE_LOCAL_POST,
        me.organization?.commonFeatureUseLimit
      ) === FeatureUseStatus.CAN_USE);

  const refreshScrollerRef = React.useRef<(() => void) | null>(null);

  const handleUploadSuccess = () => {
    refreshScrollerRef.current?.();
    queryClient.invalidateQueries(["media", locationId]);
  };

  const fetchMedia = useCallback(async (params: any) => {
    const media = await DataSvc.getMedia(params);
    return { data: media as MediaData[], count: 0 };
  }, []);

  return (
    <div className="right-content media-page">
      <div className="back-top-bar">
        <NavLink to={`/location/${locationId}`} className="back-link">
          <i className="icons icon-back"></i>
          {t("back_to_facility_details")}
        </NavLink>
      </div>

      {locationData && (
        <>
          <div className="location-name">
            <PageTitle text={locationData.name} />
            {canUploadMedia && (
              <button
                className="btn btn-blue btn-new-media"
                onClick={() => setUploadModal({})}
              >
                <Pencil size={16} color="white" className="pencil-icon" />
                {t("post")}
              </button>
            )}
          </div>

          <div className="media-category-row">
            <MediaCategorySection
              locationId={locationId}
              label={t("cover_photo")}
              category={MediaItemCategory.COVER}
              onUploadClick={canUploadMedia ? setUploadModal : undefined}
            />
            <MediaCategorySection
              locationId={locationId}
              label={t("profile_photo")}
              category={MediaItemCategory.PROFILE}
              onUploadClick={canUploadMedia ? setUploadModal : undefined}
            />
          </div>

          <div className="section-title">{t("facility_latest_media")}</div>
          <div className="recent-media-container">
            <InfiniteScroller
              direction="horizontal"
              pageSize={10}
              dependencyKey={`${locationId}`}
              fetchFunction={fetchMedia}
              initialParams={{
                order: "desc",
                sort: "post_date",
                seek_total_count: true,
                location_id: [locationId],
              }}
              noItemsComponent={<div>{t("no_media_available")}</div>}
              onRefresh={(fn) => {
                refreshScrollerRef.current = fn;
              }}
              renderItem={(item: any) => (
                <MediaItem
                  key={item.id}
                  mediaItem={item as MediaData}
                  hideLocationName
                  showDescription={true}
                />
              )}
            />
          </div>
        </>
      )}
      {uploadModal !== null && (
        <ModalUploadMedia
          locationId={locationId}
          defaults={uploadModal}
          onClose={() => setUploadModal(null)}
          onSuccess={handleUploadSuccess}
        />
      )}
    </div>
  );
};

export default withRouter(MediaPage);
